import { useState } from "react";
import { Plus, Trash, UploadSimple, Copy, CaretDown, Warning } from "@phosphor-icons/react";

const inputCls = "w-full rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-sm text-ink outline-none transition-colors focus:border-pine";
const areaCls = "w-full rounded-2xl border border-ink/10 bg-white p-4 text-sm text-ink outline-none transition-colors focus:border-pine";

const JSON_TEMPLATE = `Research this Google Business Profile: <BUSINESS NAME + MAPS LINK>.
Return STRICT JSON only (no markdown) with these keys. Use null and "not_verified" for anything you cannot confirm — never guess:
{
  "business": {"name":"","address":"","phone":"","website":"","categories":[],"rating":null,"review_count":null,"photos_count":null,"description":"","services":[],"hours":[]},
  "competitors": [{"name":"","rating":null,"review_count":null,"notes":""}],
  "executive_summary": "",
  "opportunities": [{"title":"","why":"","priority":"high"}],
  "competitor_comparison": "",
  "description_rewrite": "",
  "next_steps": [],
  "key_findings": [],
  "before_after": [{"before":"","after":""}],
  "scores": [{"key":"basics|categories|hours|rating|review_volume|photos|description|review_responses","points":null,"status":"scored|needs_review|not_verified","note":""}]
}`;

const num = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

export const AuditEditor = ({ edit, setEdit, dataSource }) => {
  const [importOpen, setImportOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [importMsg, setImportMsg] = useState("");

  const set = (patch) => setEdit((e) => ({ ...e, ...patch }));
  const setPlace = (k, v) => setEdit((e) => ({ ...e, place: { ...e.place, [k]: v } }));
  const setComp = (i, k, v) =>
    setEdit((e) => ({ ...e, competitors: e.competitors.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const setCat = (i, k, v) =>
    setEdit((e) => ({ ...e, categories: e.categories.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));

  const liveTotal = (() => {
    const scored = edit.categories.filter((c) => c.status === "scored");
    const earned = scored.reduce((s, c) => s + (Number(c.points) || 0), 0);
    const possible = scored.reduce((s, c) => s + (Number(c.max) || 0), 0);
    return possible ? Math.round((100 * earned) / possible) : null;
  })();

  const copyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(JSON_TEMPLATE);
      setImportMsg("Template copied — paste it into ChatGPT with the business details.");
    } catch {
      setImportMsg("Copy failed — select and copy the text manually.");
    }
  };

  const applyImport = () => {
    setImportMsg("");
    let j;
    try {
      j = JSON.parse(jsonText);
    } catch {
      setImportMsg("That isn't valid JSON — check for missing commas or quotes.");
      return;
    }
    const b = j.business || j.place || {};
    const arr = (v) => (Array.isArray(v) ? v.join("\n") : v ?? "");
    setEdit((e) => {
      const comps = Array.isArray(j.competitors)
        ? j.competitors.slice(0, 3).map((c) => ({
            name: c.name || "",
            rating: c.rating ?? "",
            rating_count: c.review_count ?? c.rating_count ?? "",
            address: c.address || "",
            notes: c.notes || "",
          }))
        : e.competitors;
      while (comps.length < 3) comps.push({ name: "", rating: "", rating_count: "", address: "", notes: "" });
      return {
        ...e,
        place: {
          ...e.place,
          name: b.name ?? e.place.name,
          address: b.address ?? e.place.address,
          phone: b.phone ?? e.place.phone,
          website: b.website ?? e.place.website,
          categories: Array.isArray(b.categories) ? b.categories.join(", ") : b.categories ?? e.place.categories,
          rating: b.rating ?? e.place.rating,
          rating_count: b.review_count ?? b.rating_count ?? e.place.rating_count,
          photos_count: b.photos_count ?? e.place.photos_count,
          description: b.description ?? e.place.description,
          services: b.services !== undefined ? arr(b.services) : e.place.services,
          hours: b.hours !== undefined ? arr(b.hours) : e.place.hours,
        },
        competitors: comps,
        executive_summary: j.executive_summary ?? e.executive_summary,
        competitor_comparison: j.competitor_comparison ?? e.competitor_comparison,
        description_rewrite: j.description_rewrite ?? e.description_rewrite,
        next_steps: Array.isArray(j.next_steps) ? j.next_steps.join("\n") : e.next_steps,
        opportunities: Array.isArray(j.opportunities)
          ? j.opportunities.slice(0, 5).map((o) => ({
              title: o.title || "",
              why: o.why || "",
              priority: ["high", "medium", "low"].includes(o.priority) ? o.priority : "medium",
            }))
          : e.opportunities,
        before_after: Array.isArray(j.before_after || j.copy_examples)
          ? (j.before_after || j.copy_examples).slice(0, 5).map((p) => ({ before: p.before || "", after: p.after || "" }))
          : e.before_after,
        manual_findings: Array.isArray(j.key_findings || j.manual_findings)
          ? (j.key_findings || j.manual_findings).map(String)
          : e.manual_findings,
        categories: Array.isArray(j.scores)
          ? e.categories.map((c) => {
              const s = j.scores.find((x) => x.key === c.key);
              if (!s) return c;
              return {
                ...c,
                points: s.points === null || s.points === undefined ? c.points : Number(s.points) || 0,
                max: s.max ? Number(s.max) : c.max,
                status: ["scored", "needs_review", "not_verified"].includes(s.status) ? s.status : c.status,
                note: s.note ?? c.note,
              };
            })
          : e.categories,
      };
    });
    setImportMsg("Imported — review every field below, then click Save edits.");
    setImportOpen(false);
    setJsonText("");
  };

  return (
    <div className="space-y-6" data-testid="audit-editor">
      {importMsg && (
        <p data-testid="import-message" className="rounded-2xl bg-pine-light p-4 text-sm font-medium text-pine">{importMsg}</p>
      )}

      <div className="card-surface p-6">
        <button
          type="button"
          data-testid="import-json-toggle"
          onClick={() => setImportOpen(!importOpen)}
          className="flex w-full items-center justify-between"
        >
          <span className="flex items-center gap-2 font-heading text-lg font-bold text-ink">
            <UploadSimple size={20} weight="duotone" className="text-pine" />
            Import audit JSON
          </span>
          <CaretDown size={18} className={`text-soot transition-transform duration-300 ${importOpen ? "rotate-180" : ""}`} />
        </button>
        {importOpen && (
          <div className="mt-4">
            <p className="text-sm text-soot">
              Paste JSON from ChatGPT below. Nothing is fetched or generated — importing is free.
            </p>
            <textarea
              data-testid="import-json-textarea"
              rows={7}
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              className={`${areaCls} mt-3 font-mono text-xs`}
              placeholder='{"business": {"name": "…"}, …}'
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" data-testid="import-json-button" onClick={applyImport} className="rounded-full bg-pine px-5 py-2.5 text-sm font-bold text-cream transition-colors hover:bg-pine-hover">
                Import into fields
              </button>
              <button type="button" data-testid="copy-template-button" onClick={copyTemplate} className="flex items-center gap-1.5 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-bold text-ink transition-colors hover:bg-cream">
                <Copy size={15} /> Copy ChatGPT template
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card-surface p-6">
        <h3 className="font-heading text-lg font-bold text-ink">Business profile</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><label className="field-label">Business name</label><input data-testid="edit-place-name" value={edit.place.name} onChange={(e) => setPlace("name", e.target.value)} className={`${inputCls} mt-1`} /></div>
          <div><label className="field-label">Address</label><input data-testid="edit-place-address" value={edit.place.address} onChange={(e) => setPlace("address", e.target.value)} className={`${inputCls} mt-1`} /></div>
          <div><label className="field-label">Phone</label><input data-testid="edit-place-phone" value={edit.place.phone} onChange={(e) => setPlace("phone", e.target.value)} className={`${inputCls} mt-1`} /></div>
          <div><label className="field-label">Website</label><input data-testid="edit-place-website" value={edit.place.website} onChange={(e) => setPlace("website", e.target.value)} className={`${inputCls} mt-1`} /></div>
          <div className="sm:col-span-2"><label className="field-label">Categories (comma separated)</label><input data-testid="edit-place-categories" value={edit.place.categories} onChange={(e) => setPlace("categories", e.target.value)} className={`${inputCls} mt-1`} /></div>
          <div><label className="field-label">Rating (0–5)</label><input data-testid="edit-place-rating" type="number" step="0.1" min="0" max="5" value={edit.place.rating} onChange={(e) => setPlace("rating", e.target.value)} className={`${inputCls} mt-1`} /></div>
          <div><label className="field-label">Review count</label><input data-testid="edit-place-review-count" type="number" min="0" value={edit.place.rating_count} onChange={(e) => setPlace("rating_count", e.target.value)} className={`${inputCls} mt-1`} /></div>
          <div><label className="field-label">Photos count</label><input data-testid="edit-place-photos" type="number" min="0" value={edit.place.photos_count} onChange={(e) => setPlace("photos_count", e.target.value)} className={`${inputCls} mt-1`} /></div>
        </div>
        <div className="mt-4 space-y-4">
          <div><label className="field-label">Current business description</label><textarea data-testid="edit-place-description" rows={3} value={edit.place.description} onChange={(e) => setPlace("description", e.target.value)} className={`${areaCls} mt-1`} /></div>
          <div><label className="field-label">Services (one per line)</label><textarea data-testid="edit-place-services" rows={3} value={edit.place.services} onChange={(e) => setPlace("services", e.target.value)} className={`${areaCls} mt-1`} /></div>
          <div><label className="field-label">Opening hours (one per line)</label><textarea data-testid="edit-place-hours" rows={3} value={edit.place.hours} onChange={(e) => setPlace("hours", e.target.value)} className={`${areaCls} mt-1`} placeholder={"Monday: 8:00 AM – 6:00 PM\nTuesday: 8:00 AM – 6:00 PM"} /></div>
        </div>
      </div>

      <div className="card-surface p-6">
        <h3 className="font-heading text-lg font-bold text-ink">Competitors</h3>
        <p className="mt-1 text-xs text-soot">Leave a row empty to exclude it from the report.</p>
        <div className="mt-4 space-y-4">
          {edit.competitors.map((c, i) => (
            <div key={i} className="rounded-2xl border border-ink/10 p-4" data-testid={`edit-competitor-${i}`}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <input value={c.name} onChange={(e) => setComp(i, "name", e.target.value)} placeholder={`Competitor ${i + 1} name`} className={inputCls} />
                <input type="number" step="0.1" min="0" max="5" value={c.rating ?? ""} onChange={(e) => setComp(i, "rating", e.target.value)} placeholder="Rating" className={inputCls} />
                <input type="number" min="0" value={c.rating_count ?? ""} onChange={(e) => setComp(i, "rating_count", e.target.value)} placeholder="Reviews" className={inputCls} />
              </div>
              <input value={c.notes || ""} onChange={(e) => setComp(i, "notes", e.target.value)} placeholder="Notes (optional, internal)" className={`${inputCls} mt-3`} />
            </div>
          ))}
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-bold text-ink">Scorecard</h3>
            <p className="mt-1 text-xs text-soot">
              Only "Scored" rows count toward the total — unverified rows are excluded, never penalized.
            </p>
          </div>
          <p data-testid="scorecard-live-total" className="font-heading text-4xl font-extrabold tracking-tighter text-pine">
            {liveTotal === null ? "—" : liveTotal}<span className="text-lg text-soot">/100</span>
          </p>
        </div>
        <div className="mt-5 space-y-4">
          {edit.categories.map((c, i) => (
            <div key={c.key} className="rounded-2xl border border-ink/10 p-4" data-testid={`scorecard-${c.key}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-40 flex-1">
                  <p className="text-sm font-bold text-ink">{c.label}</p>
                  <p className="text-xs text-soot">{c.rubric}</p>
                </div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-soot">
                  Pts
                  <input type="number" min="0" max={c.max} value={c.points} onChange={(e) => setCat(i, "points", e.target.value)} className="w-16 rounded-lg border border-ink/10 px-2 py-1.5 text-sm outline-none focus:border-pine" data-testid={`score-points-${c.key}`} />
                </label>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-soot">
                  Max
                  <input type="number" min="1" max="40" value={c.max} onChange={(e) => setCat(i, "max", e.target.value)} className="w-16 rounded-lg border border-ink/10 px-2 py-1.5 text-sm outline-none focus:border-pine" data-testid={`score-max-${c.key}`} />
                </label>
                <select value={c.status} onChange={(e) => setCat(i, "status", e.target.value)} className="rounded-lg border border-ink/10 px-2 py-1.5 text-xs font-semibold outline-none focus:border-pine" data-testid={`score-status-${c.key}`}>
                  <option value="scored">Scored</option>
                  <option value="needs_review">Needs manual review</option>
                  <option value="not_verified">Not verified</option>
                </select>
              </div>
              <input value={c.note || ""} onChange={(e) => setCat(i, "note", e.target.value)} placeholder="Note shown on the report" className={`${inputCls} mt-3`} data-testid={`score-note-${c.key}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="card-surface p-6">
        <h3 className="font-heading text-lg font-bold text-ink">Analysis & recommendations</h3>
        {dataSource === "manual" && (
          <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-soot">
            <Warning size={14} weight="duotone" className="text-amber-600" />
            Manual audit — no AI was used. Everything below is yours to write or import.
          </p>
        )}
        <div className="mt-5 space-y-5">
          <div><label className="field-label">Executive summary</label><textarea data-testid="edit-executive-summary" rows={4} value={edit.executive_summary} onChange={(e) => set({ executive_summary: e.target.value })} className={`${areaCls} mt-1`} /></div>
          <div>
            <label className="field-label">Priority recommendations</label>
            <div className="mt-2 space-y-3">
              {edit.opportunities.map((o, i) => (
                <div key={i} className="rounded-2xl border border-ink/10 p-4" data-testid={`edit-opportunity-${i}`}>
                  <div className="flex items-center gap-3">
                    <input value={o.title} onChange={(e) => { const ops = [...edit.opportunities]; ops[i] = { ...o, title: e.target.value }; set({ opportunities: ops }); }} className="w-full border-b border-ink/10 pb-1 text-sm font-bold text-ink outline-none focus:border-pine" placeholder="Title" />
                    <select value={o.priority} onChange={(e) => { const ops = [...edit.opportunities]; ops[i] = { ...o, priority: e.target.value }; set({ opportunities: ops }); }} className="rounded-lg border border-ink/10 px-2 py-1 text-xs outline-none focus:border-pine">
                      <option value="high">high</option><option value="medium">medium</option><option value="low">low</option>
                    </select>
                    <button type="button" aria-label="Remove opportunity" onClick={() => set({ opportunities: edit.opportunities.filter((_, j) => j !== i) })} className="text-soot hover:text-red-600"><Trash size={15} /></button>
                  </div>
                  <textarea rows={2} value={o.why} onChange={(e) => { const ops = [...edit.opportunities]; ops[i] = { ...o, why: e.target.value }; set({ opportunities: ops }); }} className="mt-2 w-full text-sm text-soot outline-none" placeholder="Why it matters" />
                </div>
              ))}
              <button type="button" data-testid="add-opportunity-button" onClick={() => set({ opportunities: [...edit.opportunities, { title: "", why: "", priority: "medium" }] })} className="flex items-center gap-1.5 text-sm font-semibold text-pine">
                <Plus size={15} weight="bold" /> Add recommendation
              </button>
            </div>
          </div>
          <div><label className="field-label">Competitor comparison (report text)</label><textarea data-testid="edit-competitor-comparison" rows={3} value={edit.competitor_comparison} onChange={(e) => set({ competitor_comparison: e.target.value })} className={`${areaCls} mt-1`} /></div>
          <div>
            <label className="field-label">Proposed rewritten description ({edit.description_rewrite.length}/750) — dashboard only, never in the PDF</label>
            <textarea data-testid="edit-description-rewrite" rows={4} maxLength={750} value={edit.description_rewrite} onChange={(e) => set({ description_rewrite: e.target.value })} className={`${areaCls} mt-1`} />
          </div>
          <div><label className="field-label">Next steps (one per line)</label><textarea data-testid="edit-next-steps" rows={4} value={edit.next_steps} onChange={(e) => set({ next_steps: e.target.value })} className={`${areaCls} mt-1`} /></div>
          <div>
            <label className="field-label">Before-and-after copy examples (shown in the PDF)</label>
            <div className="mt-2 space-y-3">
              {edit.before_after.map((p, i) => (
                <div key={i} className="rounded-2xl border border-ink/10 p-4" data-testid={`edit-before-after-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-soot">Example {i + 1}</span>
                    <button type="button" aria-label="Remove example" onClick={() => set({ before_after: edit.before_after.filter((_, j) => j !== i) })} className="text-soot hover:text-red-600"><Trash size={15} /></button>
                  </div>
                  <textarea rows={2} value={p.before} onChange={(e) => { const ba = [...edit.before_after]; ba[i] = { ...p, before: e.target.value }; set({ before_after: ba }); }} className="mt-2 w-full border-b border-ink/10 pb-1 text-sm text-soot outline-none focus:border-pine" placeholder="Before — their current copy" />
                  <textarea rows={2} value={p.after} onChange={(e) => { const ba = [...edit.before_after]; ba[i] = { ...p, after: e.target.value }; set({ before_after: ba }); }} className="mt-2 w-full border-b border-pine/30 pb-1 text-sm font-medium text-ink outline-none focus:border-pine" placeholder="After — your improved version" />
                </div>
              ))}
              <button type="button" data-testid="add-before-after-button" onClick={() => set({ before_after: [...edit.before_after, { before: "", after: "" }] })} className="flex items-center gap-1.5 text-sm font-semibold text-pine">
                <Plus size={15} weight="bold" /> Add example
              </button>
            </div>
          </div>
          <div>
            <label className="field-label">Key findings</label>
            <ul className="mt-2 space-y-2">
              {edit.manual_findings.map((f, i) => (
                <li key={i} className="flex items-center gap-3 rounded-xl bg-cream px-4 py-2.5 text-sm text-ink">
                  <span className="flex-1">{f}</span>
                  <button type="button" aria-label="Remove finding" onClick={() => set({ manual_findings: edit.manual_findings.filter((_, j) => j !== i) })} className="text-soot hover:text-red-600"><Trash size={15} /></button>
                </li>
              ))}
            </ul>
            <KeyFindingAdder onAdd={(f) => set({ manual_findings: [...edit.manual_findings, f] })} />
          </div>
        </div>
      </div>
    </div>
  );
};

const KeyFindingAdder = ({ onAdd }) => {
  const [v, setV] = useState("");
  return (
    <div className="mt-2 flex gap-2">
      <input data-testid="new-finding-input" value={v} onChange={(e) => setV(e.target.value)} className="flex-1 rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm outline-none focus:border-pine" placeholder="Add a key finding…" />
      <button type="button" data-testid="add-finding-button" onClick={() => { if (v.trim()) { onAdd(v.trim()); setV(""); } }} className="rounded-xl bg-pine px-4 py-2 text-sm font-bold text-cream">Add</button>
    </div>
  );
};

export { num };
